<template>
  <div v-if="v2" class="as-a-coach-checkpoint-inspection-card" :class="railClass">
    <ChatterDrawer
      :drawer="chatterDrawerOpen"
      :type="chatterTypeName"
      :typeValue="chatterTypeValue"
      :listData="chatterListData"
      object-type="checkpoint_evaluation"
      :object-id="resolvedInspectionId"
      :chatterList="chatterList"
      :totalCheckpointChatter="totalChatter"
      iconName="org_icon_responsibility"
      @closeDrawer="onChatterDrawerClose"
      @requestMoreCheckpointsChatter="loadMoreChatter"
    />
    <div class="as-a-coach-checkpoint-inspection-card__rail" aria-hidden="true" />

    <div class="as-a-coach-checkpoint-inspection-card__body">
      <span class="as-a-coach-checkpoint-inspection-card__title">{{ title }}</span>

      <div class="as-a-coach-checkpoint-inspection-card__meta">
        <ul class="as-a-coach-checkpoint-inspection-card__badges">
          <li
            v-if="activeFacets.isInspectorOnly"
            class="as-a-coach-checkpoint-inspection-card__badge-item"
          >
            <InspectorOnlyBadge />
          </li>
          <li
            v-if="showStatusIndicator && checkpointStatus"
            class="as-a-coach-checkpoint-inspection-card__badge-item"
          >
            <InspectionStatusIndicatorBadge :checkpoint-status="checkpointStatus" />
          </li>
          <li
            v-if="activeFacets.media === 'video'"
            class="as-a-coach-checkpoint-inspection-card__badge-item"
          >
            <InspectionVideoWatchStatusBadge
              :learning-seen="learningSeen"
              :microlearning-completed-on="microlearningCompletedOn"
              :employee-name="employeeName"
            />
          </li>
          <li
            v-else-if="activeFacets.media === 'article'"
            class="as-a-coach-checkpoint-inspection-card__badge-item"
          >
            <InspectionArticleReadStatusBadge
              :ideaMDP452="ideaMDP452"
              :learning-seen="learningSeen"
            />
          </li>
          <li v-if="showQuizBadge" class="as-a-coach-checkpoint-inspection-card__badge-item">
            <InspectionQuizStatusBadge
              :ideaMDP452="ideaMDP452"
              :quiz-status="quizBadgeStatus"
              :quiz-attempted-on="quizAttemptedOn"
              :employee-name="employeeName"
            />
          </li>
          <li v-if="showInspectedBadge" class="as-a-coach-checkpoint-inspection-card__badge-item">
            <InspectionInspectedStatusBadge
              :ideaMDP452="ideaMDP452"
              :inspection-status="inspectedBadgeStatus"
              :inspected-on="inspectedOn"
              :inspected-by="inspectedBy"
            />
          </li>
        </ul>
      </div>
    </div>

    <div class="as-a-coach-checkpoint-inspection-card__actions">
      <StandardButton
        v-for="action in resolvedActions"
        :key="action.id"
        :type="action.type"
        :variant="action.variant"
        :size="action.size || 'small'"
        :label="action.label"
        class="as-a-coach-checkpoint-inspection-card__action-btn"
        @click="onActionClick(action)"
      />

      <button
        type="button"
        class="as-a-coach-checkpoint-inspection-card__chatter"
        aria-label="Open chatter"
        @click="onChatterClick"
      >
        <img :src="chatterIconSrc" alt="" width="24" height="24" />
      </button>
    </div>
  </div>

  <div v-else class="ex-checkpoint-card">
    <AsACoachCheckpointInspectionBar
      :level="level"
      :instructions="instructions"
      :video="video"
      :showAllInstructionIcon="showAllInstructionIcon"
      :inspectionStatus="inspectionStatus"
      :evaluationData="evaluationData"
      @watchVideo="$emit('watchVideo', $event)"
      @onPass="$emit('onPass', $event)"
      @onFail="$emit('onFail', $event)"
      @onNotApplicable="$emit('onNotApplicable', $event)"
      @onClose="$emit('onClose', $event)"
      @showAllInstructions="handleShowAllInstructions"
    />
    <ExCriteriaInstructionsCell
      :criteria="criteria"
      :instructions="instructions"
      :videoThumbnail="video"
      :videoUrl="video"
      :class="{
        'card-checkpoint-area': video === null,
      }"
      class="ex-checkpoint-area"
      :disableTruncation="false"
    />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import AsACoachCheckpointInspectionBar from "@components/AsACoachCheckpointInspectionBar/AsACoachCheckpointInspectionBar.vue";
import ExCriteriaInstructionsCell from "@components/InstructionsCell/ExCriteriaInstructionsCell.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import InspectionStatusIndicatorBadge from "@components/InspectionStatusIndicatorBadge/InspectionStatusIndicatorBadge.vue";
import InspectionVideoWatchStatusBadge from "@components/InspectionVideoWatchStatusBadge/InspectionVideoWatchStatusBadge.vue";
import InspectionArticleReadStatusBadge from "@components/InspectionArticleReadStatusBadge/InspectionArticleReadStatusBadge.vue";
import InspectionQuizStatusBadge from "@components/InspectionQuizStatusBadge/InspectionQuizStatusBadge.vue";
import InspectionInspectedStatusBadge from "@components/InspectionInspectedStatusBadge/InspectionInspectedStatusBadge.vue";
import InspectorOnlyBadge from "@components/InspectorOnlyBadge/InspectorOnlyBadge.vue";
import { checkpointStatusForCardStatus } from "@components/InspectionStatusIndicatorBadge/inspectionStatusIndicatorBadgeConstants";
import {
  fetchCheckpointEvaluationChatter,
  resolveAccountIdFromStorage,
  resolveCheckpointEvaluationRecordId,
} from "@components/utils/checkpointEvaluationChatter";
import {
  CARD_STATUS_LABELS,
  normalizeCardStatus,
  resolveCardTypeFacets,
} from "@components/AsEmployeeCheckpointInspectionCard/asEmployeeCheckpointInspectionCardConstants";
import {
  INSPECTED_BADGE_STATUS_BY_CARD_STATUS,
  QUIZ_BADGE_STATUS_BY_CARD_STATUS,
  isLearningSeen,
} from "@components/AsEmployeeCheckpointInspectionCard/asEmployeeCheckpointInspectionCardHelpers";
import {
  CARD_TYPES,
  CHIP_VARIANTS,
  coachRailTone,
} from "./asACoachCheckpointInspectionCardConstants";

const chatterIconSrc = require("@components/assets/icons/chatter.svg");

const props = defineProps({
  v2: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: "",
    validator: (value) => !value || CARD_TYPES.includes(value),
  },
  status: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    default: "",
  },
  /**
   * @deprecated ER-11601 — the badge row is now derived from `type` + `status`,
   * so this prop is ignored. Kept accepted (and validated) for one release so
   * legacy callers and Storybook history keep working.
   */
  statusChips: {
    type: Array,
    default: () => [],
    validator: (chips) =>
      chips.every(
        (chip) => chip && typeof chip.label === "string" && CHIP_VARIANTS.includes(chip.variant)
      ),
  },
  actionLabel: {
    type: String,
    default: "",
  },
  secondaryActionLabel: {
    type: String,
    default: "",
  },
  level: {
    type: String,
    validator: function (value) {
      return !value ||
        ["Apprentice", "Professional", "Proficient", "Coach", "Master", "Architect"].indexOf(value) !== -1;
    },
  },
  criteria: {
    type: String,
    default: null,
  },
  instructions: {
    type: String,
    default: null,
  },
  video: {
    type: String,
    default: null,
  },
  inspectionStatus: {
    type: String,
    default: "pending-evaluation",
  },
  showAllInstructionIcon: {
    type: Boolean,
    default: true,
  },
  evaluationData: {
    type: Object,
    default: () => ({
      isPassed: false,
      isFailed: false,
      isNotApplicable: false,
      first_name: "",
      last_name: "",
      updated_on: "",
      updated_by: {},
      avatar: "",
      failed_reason: "",
      not_applicable_reason: "",
      reset_reason: "",
    }),
  },
  /** Medium facet. Authoritative when supplied; otherwise parsed from `type`. */
  media: {
    type: String,
    default: null,
    validator: (value) => value == null || ["video", "article", "none"].includes(value),
  },
  /** Quiz facet (nullable so absence means "derive from type"). */
  hasQuiz: {
    type: Boolean,
    default: null,
  },
  /** Inspection facet (nullable so absence means "derive from type"). */
  hasInspection: {
    type: Boolean,
    default: null,
  },
  /**
   * Not Applicable is a universal card of its own. When true it overrides the
   * facets and status (matching AsEmployeeCheckpointInspectionCard).
   */
  notApplicable: {
    type: Boolean,
    default: false,
  },
  /**
   * Feature flag `idea-MDP-452`. Forwards to the flagged badge components
   * (kill-switch semantics, matching AsEmployeeCheckpointInspectionCard).
   */
  ideaMDP452: {
    type: Boolean,
    default: true,
  },
  /** Employee's own display name — used for the video/quiz tooltips (ER-11856). */
  employeeName: {
    type: String,
    default: "",
  },
  /** Timestamp the employee completed the microlearning video (ER-11856). */
  microlearningCompletedOn: {
    type: String,
    default: null,
  },
  /** Timestamp of the employee's most recent quiz attempt (ER-11856). */
  quizAttemptedOn: {
    type: String,
    default: null,
  },
  /** Inspector name (or AccessLimitedSerializer {name, picture} object) (ER-11856). */
  inspectedBy: {
    type: [String, Object],
    default: null,
  },
  /** Timestamp the inspection was completed (ER-11856). */
  inspectedOn: {
    type: String,
    default: null,
  },
  /** Account id for checkpoint_evaluation chatter; falls back to `currentAccountID` in localStorage. */
  accountId: {
    type: [String, Number],
    default: null,
  },
  /** Checkpoint evaluation / inspection id for chatter `record_id`. */
  inspectionId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits([
  "watchVideo",
  "onPass",
  "onFail",
  "onNotApplicable",
  "onClose",
  "showAllInstructions",
  "action-click",
  "secondary-action-click",
  "chatter-click",
]);

const handleShowAllInstructions = (data) => {
  emit("showAllInstructions", {
    ...data,
    criteria: props.criteria,
  });
};

/** Normalised status enum; empty string when the legacy status is unrecognised. */
const normalizedStatus = computed(() => normalizeCardStatus(props.status) || "");

const parsedTypeFacets = computed(() => resolveCardTypeFacets(props.type));

const notApplicable = computed(
  () =>
    props.notApplicable === true ||
    normalizedStatus.value === "not_applicable" ||
    parsedTypeFacets.value.notApplicable
);

const baseFacets = computed(() => {
  const parsed = parsedTypeFacets.value;
  return {
    media: props.media != null ? props.media : parsed.media,
    hasQuiz: props.hasQuiz != null ? props.hasQuiz : parsed.hasQuiz,
    hasInspection: props.hasInspection != null ? props.hasInspection : parsed.hasInspection,
    isInspectorOnly:
      parsed.isInspectorOnly ||
      (props.media === "none" && props.hasQuiz === false && props.hasInspection === true),
  };
});

/** Facets that actually drive the badge row; notApplicable overrides everything. */
const activeFacets = computed(() => {
  if (notApplicable.value) {
    return { media: "none", hasQuiz: false, hasInspection: false, isInspectorOnly: false };
  }
  return baseFacets.value;
});

const railTone = computed(() => coachRailTone(props.status, props.type));

const railClass = computed(() => ({
  [`as-a-coach-checkpoint-inspection-card--rail-${railTone.value}`]: true,
}));

/** Inspector-only cards never render the coarse status indicator badge. */
const showStatusIndicator = computed(() => !activeFacets.value.isInspectorOnly);

/** Coarse "Checkpoint …" status for the status indicator (via the badge module's own map). */
const checkpointStatus = computed(() => {
  if (!showStatusIndicator.value) return "";
  const canonical = CARD_STATUS_LABELS[normalizedStatus.value];
  return checkpointStatusForCardStatus(canonical) || "";
});

const learningSeen = computed(() => isLearningSeen(normalizedStatus.value));

const quizBadgeStatus = computed(() => {
  if (!activeFacets.value.hasQuiz) return "Not Required";
  return QUIZ_BADGE_STATUS_BY_CARD_STATUS[normalizedStatus.value] || "Not Attempted";
});

const inspectedBadgeStatus = computed(() => {
  // Inspector-only checkpoints have no inspection step facet, but their card's
  // single status signal IS the inspection badge — derive it from the status.
  if (activeFacets.value.isInspectorOnly) {
    return INSPECTED_BADGE_STATUS_BY_CARD_STATUS[normalizedStatus.value] || "inspection-pending";
  }
  if (!activeFacets.value.hasInspection) return "not-required";
  return INSPECTED_BADGE_STATUS_BY_CARD_STATUS[normalizedStatus.value] || "inspection-pending";
});

/** Quiz badge is suppressed on Inspector-only and the universal Not Applicable card. */
const showQuizBadge = computed(() => !notApplicable.value && !activeFacets.value.isInspectorOnly);

/** Inspected badge is suppressed on the Not Applicable card only. */
const showInspectedBadge = computed(() => !notApplicable.value);

/**
 * Coach CTA set is keyed on the normalised status enum (ER-11601) so both the
 * legacy vocabulary from `buildChecklistInspectionLevels.resolveCardStatus`
 * ("Not started", "Inspection failed", …) and the new enum (`not_started`,
 * `inspection_failed`, …) resolve identically, and no Figma state falls through
 * to "no buttons".
 */
function defaultActionsForStatus(status) {
  switch (status) {
    case "not_started":
    case "quiz_not_attempted":
    case "quiz_failed":
      return [
        {
          id: "mark-not-applicable",
          label: "Mark as N/A",
          type: "magenta",
          variant: "outline",
          size: "small",
        },
      ];
    case "inspection_ready":
      return [
        {
          id: "inspect-now",
          label: "Inspect now",
          type: "magenta",
          variant: "primary",
          size: "small",
        },
        {
          id: "mark-not-applicable",
          label: "Mark as N/A",
          type: "magenta",
          variant: "outline",
          size: "small",
        },
      ];
    case "inspection_failed":
      return [
        {
          id: "re-inspect",
          label: "Re-inspect",
          type: "magenta",
          variant: "primary",
          size: "small",
        },
        {
          id: "mark-not-applicable",
          label: "Mark as N/A",
          type: "magenta",
          variant: "outline",
          size: "small",
        },
      ];
    case "passed":
    case "not_applicable":
      return [
        {
          id: "reset",
          label: "Reset",
          type: "magenta",
          variant: "outline",
          size: "small",
        },
      ];
    default:
      return [];
  }
}

const resolvedActions = computed(() => {
  const actions = defaultActionsForStatus(normalizedStatus.value).map((action) => ({ ...action }));
  if (!actions.length) return actions;

  if (props.actionLabel) {
    actions[0].label = props.actionLabel;
  }
  if (actions.length > 1 && props.secondaryActionLabel) {
    actions[1].label = props.secondaryActionLabel;
  }
  return actions;
});

function onActionClick(action) {
  if (action.id === "mark-not-applicable" && resolvedActions.value.length > 1) {
    emit("secondary-action-click", {
      id: action.id,
      label: action.label,
      status: props.status,
    });
    return;
  }
  emit("action-click", { id: action.id, label: action.label, status: props.status });
}

const chatterDrawerOpen = ref(false);
const chatterList = ref([]);
const totalChatter = ref(0);
const chatterPage = ref(1);
const chatterLoading = ref(false);
const chatterTypeName = ref("Checkpoint");
const chatterTypeValue = ref("");
const chatterListData = ref({});

const resolvedInspectionId = computed(() =>
  resolveCheckpointEvaluationRecordId({ inspectionId: props.inspectionId })
);

const resolvedAccountId = computed(() => resolveAccountIdFromStorage(props.accountId));

async function fetchChatter(page = 1, append = false) {
  const accountId = resolvedAccountId.value;
  const recordId = resolvedInspectionId.value;
  if (accountId == null || recordId == null || chatterLoading.value) return;

  chatterLoading.value = true;
  try {
    const { results, count } = await fetchCheckpointEvaluationChatter(accountId, recordId, page);
    totalChatter.value = count;
    chatterList.value = append ? [...chatterList.value, ...results] : results;
    chatterPage.value = page;
  } catch (error) {
    console.error("Error loading checkpoint evaluation chatter:", error);
  } finally {
    chatterLoading.value = false;
  }
}

async function openChatterDrawer() {
  if (chatterLoading.value) return;

  chatterTypeValue.value = props.title || props.criteria || "";
  chatterListData.value = {
    id: resolvedInspectionId.value,
    criteria: props.title || props.criteria || "",
  };
  chatterList.value = [];
  totalChatter.value = 0;
  chatterPage.value = 1;
  await fetchChatter(1, false);
  chatterDrawerOpen.value = true;
}

async function loadMoreChatter() {
  if (totalChatter.value <= chatterList.value.length) return;
  await fetchChatter(chatterPage.value + 1, true);
}

function onChatterDrawerClose(data) {
  if (!data) return;
  chatterDrawerOpen.value = false;
  chatterList.value = [];
  totalChatter.value = 0;
  chatterPage.value = 1;
}

function onChatterClick() {
  if (chatterLoading.value) return;
  emit("chatter-click");
  openChatterDrawer();
}
</script>

<style>
.ex-checkpoint-card {
  display: flex;
  flex-direction: column;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 8px 8px 8px 8px;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}
.ex-checkpoint-area {
  margin-top: 0px;
  width: 100%;
  overflow: hidden;
}
.ex-checkpoint-card .ex-right-bar {
  margin-top: 5px;
}
.card-checkpoint-area {
  margin: 6px 0px 0px;
}
</style>

<style scoped>
.as-a-coach-checkpoint-inspection-card {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  overflow: hidden;
  font-family:
    Inter,
    system-ui,
    -apple-system,
    "Segoe UI",
    Roboto,
    Arial,
    sans-serif;
}

.as-a-coach-checkpoint-inspection-card__rail {
  flex: 0 0 4px;
  align-self: stretch;
  width: 4px;
  background-color: #98a2b3;
}

.as-a-coach-checkpoint-inspection-card--rail-grey .as-a-coach-checkpoint-inspection-card__rail {
  background-color: #98a2b3;
}

.as-a-coach-checkpoint-inspection-card--rail-orange .as-a-coach-checkpoint-inspection-card__rail {
  background-color: #f79009;
}

.as-a-coach-checkpoint-inspection-card--rail-red .as-a-coach-checkpoint-inspection-card__rail {
  background-color: #f04438;
}

.as-a-coach-checkpoint-inspection-card--rail-green .as-a-coach-checkpoint-inspection-card__rail {
  background-color: #17b26a;
}

.as-a-coach-checkpoint-inspection-card--rail-purple .as-a-coach-checkpoint-inspection-card__rail {
  background-color: #6734d4;
}

.as-a-coach-checkpoint-inspection-card--rail-blue .as-a-coach-checkpoint-inspection-card__rail {
  background-color: #3538cd;
}

.as-a-coach-checkpoint-inspection-card__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 11px 14px;
}

.as-a-coach-checkpoint-inspection-card__title {
  color: #101828;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
}

.as-a-coach-checkpoint-inspection-card__meta {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.as-a-coach-checkpoint-inspection-card__badges {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.as-a-coach-checkpoint-inspection-card__badge-item {
  display: inline-flex;
  margin: 0;
}

.as-a-coach-checkpoint-inspection-card__actions {
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  gap: 10px;
  padding: 11px 14px;
  border-left: 1px solid #f2f4f7;
  box-sizing: border-box;
}

.as-a-coach-checkpoint-inspection-card__chatter {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  box-sizing: border-box;
}

.as-a-coach-checkpoint-inspection-card__chatter:hover {
  background-color: #f2edff;
}

.as-a-coach-checkpoint-inspection-card__chatter:focus-visible {
  outline: 2px solid #c3b5fd;
  outline-offset: 2px;
}
</style>
